import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Rocket, TrendingUp, Target, Brain, Building2, DollarSign, Globe } from 'lucide-react';
import axios from 'axios';
import NewsFeed from '@/components/NewsFeed';
import { useCountUp } from '@/hooks/useCountUp';

const LandingPage = () => {
  const [stats, setStats] = useState({
    total_startups: 0,
    total_revenue_tracked: 0,
    industries_represented: 0
  });
  const [loading, setLoading] = useState(true);

  const animatedStartups = useCountUp(stats.total_startups, 2000);
  const animatedRevenue = useCountUp(stats.total_revenue_tracked, 2000);
  const animatedIndustries = useCountUp(stats.industries_represented, 2000);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${BACKEND_URL}/api/public/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}K`;
    }
    return `$${value.toFixed(0)}`;
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleLogin = () => {
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleDemoLogin = () => {
    console.log('Demo login clicked');
    // Redirect with demo parameter
    window.location.href = '/?demo=premium';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-cyan-900/20 via-slate-900/0 to-slate-900/0"></div>
        
        <nav className="relative z-10 container mx-auto px-6 py-6 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center">
              <Rocket className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              StartupBlackbox
            </span>
          </div>
          <Button 
            onClick={handleLogin}
            type="button"
            data-testid="nav-login-btn"
            className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white px-6 py-2 rounded-full font-medium"
          >
            Sign Up / Log In
          </Button>
        </nav>

        <div className="relative z-10 container mx-auto px-6 pt-20 pb-32">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
              Unlock Your Startup's
              <span className="block bg-gradient-to-r from-cyan-400 via-blue-500 to-blue-600 bg-clip-text text-transparent">
                Hidden Potential
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
              Track critical metrics, make data-driven decisions, and accelerate your growth journey. 
              Everything you need to measure what matters.
            </p>
            <Button
              onClick={handleLogin}
              data-testid="hero-get-started-btn"
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white px-10 py-6 text-lg rounded-full font-semibold shadow-lg shadow-cyan-500/30"
            >
              Get Started Free
            </Button>
          </div>
        </div>
      </div>

      {/* Mini Dashboard Stats */}
      <div className="relative py-16 bg-slate-900/80">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-center mb-12 text-slate-200">
              Join the Growing Community
            </h2>
            
            {loading ? (
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-cyan-400"></div>
              </div>
            ) : (
              <div className="grid md:grid-cols-3 gap-8">
                {/* Total Startups */}
                <div className="glass-effect p-8 rounded-2xl text-center group hover:border-cyan-500/50 transition-all" data-testid="stat-startups">
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <Building2 className="w-8 h-8 text-cyan-400" />
                  </div>
                  <div className="text-5xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent mb-2">
                    {animatedStartups}
                  </div>
                  <p className="text-slate-400 font-medium">Startups Registered</p>
                </div>

                {/* Total Revenue */}
                <div className="glass-effect p-8 rounded-2xl text-center group hover:border-cyan-500/50 transition-all" data-testid="stat-revenue">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-500/20 to-emerald-600/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <DollarSign className="w-8 h-8 text-green-400" />
                  </div>
                  <div className="text-5xl font-bold bg-gradient-to-r from-green-400 to-emerald-500 bg-clip-text text-transparent mb-2">
                    {formatCurrency(animatedRevenue)}
                  </div>
                  <p className="text-slate-400 font-medium">Revenue Being Tracked</p>
                </div>

                {/* Industries */}
                <div className="glass-effect p-8 rounded-2xl text-center group hover:border-cyan-500/50 transition-all" data-testid="stat-industries">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500/20 to-pink-600/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <Globe className="w-8 h-8 text-purple-400" />
                  </div>
                  <div className="text-5xl font-bold bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent mb-2">
                    {animatedIndustries}
                  </div>
                  <p className="text-slate-400 font-medium">Industries Represented</p>
                </div>
              </div>
            )}

            <div className="text-center mt-10">
              <p className="text-slate-400 text-sm">
                Real-time data from our community of innovators
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="relative py-24 bg-slate-900/50">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16">
            Everything You Need to <span className="text-cyan-400">Scale Smart</span>
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="glass-effect p-8 rounded-2xl hover:border-cyan-500/50 transition-all group">
              <div className="w-14 h-14 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <TrendingUp className="w-7 h-7 text-cyan-400" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Smart Metrics Tracking</h3>
              <p className="text-slate-400">
                Monitor MRR, CAC, LTV, churn rate, and more. Interactive flip cards reveal calculations and insights.
              </p>
            </div>

            <div className="glass-effect p-8 rounded-2xl hover:border-cyan-500/50 transition-all group">
              <div className="w-14 h-14 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Brain className="w-7 h-7 text-cyan-400" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Knowledge Repository</h3>
              <p className="text-slate-400">
                Learn business concepts with our interactive glossary. Understand CAC, LTV, PMF, and critical startup metrics.
              </p>
            </div>

            <div className="glass-effect p-8 rounded-2xl hover:border-cyan-500/50 transition-all group">
              <div className="w-14 h-14 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Target className="w-7 h-7 text-cyan-400" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Professional Reports</h3>
              <p className="text-slate-400">
                Download comprehensive PDF reports with trends analysis, industry benchmarks, and actionable insights.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* News Feed */}
      <NewsFeed />

      {/* CTA Section */}
      <div className="relative py-24">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6">
            Ready to Take Control?
          </h2>
          <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
            Join startups tracking their path to success. Start measuring what matters today.
          </p>
          <Button
            onClick={handleLogin}
            data-testid="cta-get-started-btn"
            className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white px-10 py-6 text-lg rounded-full font-semibold shadow-lg shadow-cyan-500/30"
          >
            Start Free Now
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-8">
        <div className="container mx-auto px-6 flex justify-between items-center text-slate-500">
          <p>&copy; 2025 StartupBlackbox. Track smarter, grow faster.</p>
          <a
            href="/admin/login"
            className="text-slate-600 hover:text-slate-400 text-sm transition-colors"
            data-testid="admin-link"
          >
            Admin
          </a>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;